import { css } from '@emotion/react';
import { toast } from 'react-toastify';
import { useQuery } from 'react-query';
import { groupBy, zip } from 'lodash';
import { FC, useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { Flex, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { colors, fontStyles, spacing } from '../../../styles';
import Button from '../../Button';
import { getStockStats, getFinancials } from '../../../apis/backend';
import ErrorSection from '../../ErrorSection';
import buildStats from './buildStats';
import CompareSearchTakeover from './CompareSearchTakeover';
import FinancialsChart from './FinancialsChart';
import MetricChips from './MetricChips';
import FinancialStatements from './FinancialStatements';

interface Props {
  baseSymbol: string;
}

const KeyStatsPage: FC<Props> = ({ baseSymbol }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [isCompareSearchShown, setIsCompareSearchShown] = useState<boolean>();
  const [comparisonSymbol, setComparisonSymbol] = useState<string>();
  const [selectedMetric, setSelectedMetric] = useState<string>('totalRevenue');
  const [periodType, setPeriodType] = useState<'quarterly' | 'annual' | 'ttm'>('quarterly');

  const errorToastId = 'getComparisonStatsError';

  // Stats query (for key stats table)
  const baseStatsQuery = useQuery(['investments', baseSymbol, 'stats'], async () => getStockStats(baseSymbol));

  // Financials query (for chart and statements)
  const financialsQuery = useQuery(['investments', baseSymbol, 'financials'], async () => getFinancials(baseSymbol));

  // Comparison stats query
  const comparisonStatsQuery = useQuery(
    ['investments', comparisonSymbol, 'stats'],
    async () => (comparisonSymbol ? getStockStats(comparisonSymbol) : undefined),
    {
      enabled: !!comparisonSymbol,
      onError: () => {
        toast(
          () => (
            <Button
              css={css`
                width: 100%;
                display: flex;
                justify-content: space-between;
                outline: none;
                color: ${colors.liquorice};
              `}
              onClick={() => {
                toast.dismiss(errorToastId);
                comparisonStatsQuery.refetch();
              }}
            >
              <span>{t('symbol.problemLoadingData', { symbol: comparisonSymbol })}</span>
              <span
                css={css`
                  color: ${colors.petrolBlue};
                  margin-left: 4px;
                `}
              >
                {t('symbol.retry')}
              </span>
            </Button>
          ),
          { toastId: errorToastId, autoClose: false },
        );
      },
    },
  );

  // Comparison financials query (for chart)
  const comparisonFinancialsQuery = useQuery(
    ['investments', comparisonSymbol, 'financials'],
    async () => (comparisonSymbol ? getFinancials(comparisonSymbol) : undefined),
    {
      enabled: !!comparisonSymbol,
    },
  );

  useEffect(() => {
    return () => {
      toast.dismiss(errorToastId);
    };
  }, []);

  const handleStatClick = (statName: string) => {
    history.push('/chat', { message: `What does ${statName} mean for ${baseSymbol}?`, from: 'symbol' });
  };

  const handleClearComparison = () => {
    setComparisonSymbol(undefined);
  };

  if (baseStatsQuery.isLoading) return <div />;
  if (baseStatsQuery.isError || !baseStatsQuery.data) {
    return (
      <div
        css={css`
          margin-top: 40vh;
        `}
      >
        <ErrorSection text={t('symbol.statsError')} onRetry={() => baseStatsQuery.refetch()} />
      </div>
    );
  }

  // TTM mode uses quarterly data since it calculates rolling sum of 4 quarters
  const statements = periodType === 'annual' ? financialsQuery.data?.annual : financialsQuery.data?.quarterly;
  const comparisonStatements =
    periodType === 'annual' ? comparisonFinancialsQuery.data?.annual : comparisonFinancialsQuery.data?.quarterly;

  // Build stats for table - use empty object for comparison if not available
  const items = buildStats(baseStatsQuery.data, {}, baseStatsQuery.data.currency);
  const comparisonItems = comparisonStatsQuery.data
    ? buildStats(comparisonStatsQuery.data, {}, comparisonStatsQuery.data.currency)
    : null;

  const itemsBySection = groupBy(zip(items, comparisonItems), ([item]: any) => item.section);

  const isComparing = !!comparisonSymbol;

  return (
    <div css={styles.keyStats}>
      <CompareSearchTakeover
        baseSymbol={baseSymbol}
        isShown={!!isCompareSearchShown}
        onClose={() => setIsCompareSearchShown(false)}
        onSelectComparison={(symbol) => {
          setIsCompareSearchShown(false);
          setComparisonSymbol(symbol);
        }}
      />

      {/* Header */}
      <div css={styles.headingWrapper}>
        <h2 css={styles.heading}>{t('symbol.keyStats')}</h2>
        <Button onClick={() => setIsCompareSearchShown(true)} css={styles.compareButton}>
          {t('symbol.compare')}
        </Button>
      </div>

      <div css={styles.subheaderRow}>
        <span css={styles.helperText}>{t('symbol.tapStatExplanation')}</span>
        {baseStatsQuery.data.latestReportDate && (
          <span css={styles.helperText}>
            {t('symbol.quarter')}: {new Date(baseStatsQuery.data.latestReportDate).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Comparison banner */}
      {isComparing && (
        <Flex css={styles.compareBanner} justify="space-between" align="center">
          <Text css={fontStyles.cat}>
            {t('symbol.comparing')}: <strong>{baseSymbol}</strong> {t('symbol.vs')} <strong>{comparisonSymbol}</strong>
          </Text>
          <Button onClick={handleClearComparison} css={styles.clearButton}>
            ✕
          </Button>
        </Flex>
      )}

      {/* Chart Section */}
      {financialsQuery.data && (
        <>
          <FinancialsChart
            data={statements}
            comparisonData={comparisonStatements}
            baseSymbol={baseSymbol}
            comparisonSymbol={comparisonSymbol}
            metric={selectedMetric}
            periodType={periodType}
            onPeriodChange={setPeriodType}
            currency={baseStatsQuery.data.currency}
          />
          <MetricChips selected={selectedMetric} onSelect={setSelectedMetric} />
        </>
      )}

      {/* Financial Statements with integrated metrics - shown when NOT comparing */}
      {!isComparing && financialsQuery.data && (
        <FinancialStatements
          data={statements}
          stats={baseStatsQuery.data}
          periodType={periodType}
          onPeriodChange={setPeriodType}
          currency={baseStatsQuery.data.currency}
        />
      )}

      {/* Key Stats Comparison Table - only shown when comparing */}
      {isComparing && (
        <>
          <table css={styles.table}>
            {Object.keys(itemsBySection).map((section, index) => (
              <tbody key={section}>
                <tr>
                  <th css={[styles.sectionHeader, index > 0 && styles.notFirstSectionHeader, styles.leftColumn]}>
                    {section}
                  </th>
                  <th css={[styles.sectionHeader, index > 0 && styles.notFirstSectionHeader]}>{baseSymbol}</th>
                  {comparisonSymbol && (
                    <th css={[styles.sectionHeader, index > 0 && styles.notFirstSectionHeader]}>{comparisonSymbol}</th>
                  )}
                </tr>
                {itemsBySection[section].map(([baseItem, comparisonItem]: any) => (
                  <tr key={baseItem.id}>
                    <th css={[styles.statHeader, styles.leftColumn]} onClick={() => handleStatClick(baseItem.name)}>
                      {baseItem.name}
                    </th>
                    <td css={styles.value}>{baseItem.displayValue}</td>
                    {comparisonItem && <td css={styles.value}>{comparisonItem.displayValue}</td>}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
          <Text css={styles.footer}>{t('symbol.tapMetricAsk')}</Text>
        </>
      )}
    </div>
  );
};

const styles = {
  keyStats: css`
    position: relative;
  `,
  headingWrapper: css`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-bottom: ${spacing.centi};
  `,
  heading: css`
    margin: 0;
    ${fontStyles.shark};
  `,
  compareButton: css`
    padding-bottom: 2px;
    ${fontStyles.mouse};
  `,
  subheaderRow: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${spacing.deci};
  `,
  helperText: css`
    ${fontStyles.mouse};
    color: ${colors.tungsten};
    html[data-theme='dark'] & {
      color: ${colors.fog};
    }
  `,
  compareBanner: css`
    background-color: ${colors.fog};
    padding: ${spacing.centi} ${spacing.deci};
    border-radius: 8px;
    margin-bottom: ${spacing.deci};
    html[data-theme='dark'] & {
      background-color: ${colors.liquorice};
    }
  `,
  clearButton: css`
    ${fontStyles.cat};
    padding: 0;
    color: ${colors.tungsten};
    html[data-theme='dark'] & {
      color: ${colors.cloud};
    }
  `,
  table: css`
    text-align: right;
    width: 100%;
    margin-top: ${spacing.deci};
  `,
  sectionHeader: css`
    ${fontStyles.lion};
  `,
  notFirstSectionHeader: css`
    padding-top: ${spacing.deci};
  `,
  leftColumn: css`
    text-align: left;
  `,
  statHeader: css`
    ${fontStyles.cat};
    cursor: pointer;
    color: ${colors.petrolBlue};
    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
    }
  `,
  value: css`
    ${fontStyles.cat};
  `,
  footer: css`
    ${fontStyles.flea};
    color: ${colors.tungsten};
    text-align: center;
    margin-top: ${spacing.deci};
    html[data-theme='dark'] & {
      color: ${colors.cloud};
    }
  `,
};

export default KeyStatsPage;
